// Multi-select chip picker (MultiSelect) — party/role pickers in the Case and
// collective-case modals, assignee editor in CaseEventRow — moved verbatim
// from src/App.tsx (B.6, extract-don't-improve).
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "../i18n";
import { pname } from "../utils";
import { RAD, FS, Z } from "../lib/tokens";
import RolePicker from "./RolePicker";
import { AddButton } from "./SharedUI";
import type { IdPredicate } from "../lib/fnTypes"; // 214 — a callback's shape without a function type literal

export default function MultiSelect({
  l,
  opts,
  sel,
  onChange,
  onNew,
  locked,
  onRemove,
  colKey,
  isOpen,
  onToggle,
  onViewItem,
  roleEntries,
  roleMap,
  junctionType,
  titleField,
  chipExtra,
  /* 214 §3 [RULED 52] — a chip the family holds apart (a suspended counsel: greyed, after the acting ones —
     the caller orders `sel`). A predicate, like `chipExtra`: the picker learns no fact about counsel. */
  chipMuted,
}: any) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<any>(false);
  const [q, setQ] = useState<any>("");
  const ref = useRef<any>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: any) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setQ("");
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  useEffect(() => {
    if (locked) setOpen(false);
  }, [locked]);
  const av = opts.filter((o: any) => !sel.includes(o.id));
  const filtered = q
    ? av.filter((o: any) => pname(o).toLowerCase().includes(q.toLowerCase()))
    : av;
  const _newSg = l.endsWith("s") ? l.slice(0, -1) : l;
  const newLbl = "+ " + _newSg;
  /* Phase 10 — per-party role mode. Active when roleEntries is supplied: each
   chip carries an embedded RolePicker and onChange is called (ids, roleMap). */
  const roleMode = !!roleEntries;
  /* 141 §2.2 — ONE OPTIONAL SLOT ON THE CHIP, and the party's status goes in
     it. A RENDER PROP and not a `clientStatus` prop: this primitive mounts for
     nine party families in the case view, for the collective-case modal and
     for CaseEventRow's assignees, and only ONE of those has a per-matter
     lifecycle. Teaching MultiSelect about client statuses would put a fact
     about clients inside the picker that also draws experts and arbiters.
     It is `chipExtra` (camelCase, invoked — never mounted as `<ChipExtra/>`),
     because a PascalCase function declared in a render body and mounted as JSX
     is the Bug-A anti-pattern `scripts/lint-inline-components.js` fails CI on.
     Rendered on BOTH chip arms (rule 2, union not subset): the prop means the
     same thing whether or not the family carries roles. */
  // `unknown` and not `any`: this file's ratchet is at 16 and may never grow,
  // and new code that needs a type gets one rather than a new baseline.
  const extra = (id: unknown) => (chipExtra ? chipExtra(id) : null);
  const mutedFn = chipMuted as IdPredicate | undefined; // typed once: the props are `any`
  const muted = (id: unknown) => (mutedFn ? mutedFn(id) : false);
  const rmap = roleMap || {};
  const addChip = (id: any) => {
    if (roleMode) {
      const nm = rmap[id] ? rmap : { ...rmap, [id]: [] };
      onChange([...sel, id], nm);
    } else onChange([...sel, id]);
  };
  const removeChip = (id: any) => {
    if (roleMode)
      onChange(
        sel.filter((s: any) => s !== id),
        rmap,
      );
    else onChange(sel.filter((s: any) => s !== id));
  };
  const chips = (
    <div className="multiselect" style={colKey !== undefined ? { marginTop: 2 } : {}}>
      {sel.map((id: any) => {
        const it = opts.find((o: any) => o.id === id);
        if (!it) return null;
        const nameRow = (
          <span style={{ display: "inline-flex", alignItems: "center" }}>
            <span
              onClick={() => onViewItem && onViewItem(it, colKey)}
              style={{
                cursor: onViewItem ? "pointer" : "default",
                padding: "0 3px",
              }}
              title={t("ms.view_details", {
                fallback: "Click to view details",
              })}
            >
              {pname(it)}
            </span>
            {titleField && it[titleField] ? (
              <span
                style={{
                  fontSize: FS.s8,
                  marginLeft: 3,
                  background: "var(--bi)",
                  color: "var(--ts)",
                  borderRadius: RAD.r3,
                  padding: "0 3px",
                  lineHeight: "13px",
                  flexShrink: 0,
                }}
              >
                {it[titleField]}
              </span>
            ) : null}
            {!locked && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove ? onRemove(id, pname(it)) : removeChip(id);
                }}
                title={t("ms.remove_from_case", {
                  fallback: "Remove from case",
                })}
                style={{
                  marginLeft: 8,
                  padding: 0,
                  width: 16,
                  height: 16,
                  background: "rgba(248,113,113,.15)",
                  border: "1px solid rgba(248,113,113,.4)",
                  color: "var(--rd)",
                  borderRadius: RAD.r4,
                  cursor: "pointer",
                  fontSize: FS.s11,
                  lineHeight: "12px",
                  fontFamily: "inherit",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                ×
              </button>
            )}
          </span>
        );
        if (roleMode)
          return (
            <span
              key={id}
              className="multi-chip"
              data-chip-muted={muted(id) ? "1" : undefined}
              style={{
                display: "inline-flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 2,
                padding: "3px 5px",
                ...(muted(id) ? { opacity: 0.55 } : {}),
              }}
            >
              {nameRow}
              <span
                style={{
                  display: "inline-flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <RolePicker
                  roles={rmap[id] || []}
                  onChange={(nr) => onChange(sel, { ...rmap, [id]: nr })}
                  entries={roleEntries}
                  junctionType={junctionType}
                  disabled={locked}
                />
                {extra(id)}
              </span>
            </span>
          );
        return (
          <span
            key={id}
            className="multi-chip"
            data-chip-muted={muted(id) ? "1" : undefined}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "2px 4px",
              ...(muted(id) ? { opacity: 0.55 } : {}),
            }}
          >
            {nameRow}
            {extra(id)}
          </span>
        );
      })}
      {!sel.length && (
        <span style={{ color: "var(--tm)", fontSize: FS.s9, padding: 1 }}>
          {t("common.none", { fallback: "None" })}
        </span>
      )}
    </div>
  );
  const drop = open && !locked && (
    <div
      style={{
        position: "absolute",
        top: "100%",
        left: 0,
        right: 0,
        zIndex: Z.popoverBackdrop,
        background: "var(--bc)",
        border: "1px solid var(--br)",
        borderRadius: RAD.r6,
        boxShadow: "0 6px 20px rgba(0,0,0,.7)",
        overflow: "hidden",
      }}
    >
      <input
        autoFocus
        className="field-input"
        style={{
          margin: 4,
          width: "calc(100% - 8px)",
          boxSizing: "border-box",
          fontSize: FS.s11,
        }}
        placeholder={t("common.search_placeholder")}
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <div style={{ maxHeight: 160, overflowY: "auto" }}>
        {filtered.length === 0 && !onNew && (
          <div
            style={{
              padding: "6px 10px",
              fontSize: FS.s10_5,
              color: "var(--tm)",
              fontStyle: "italic",
            }}
          >
            {t("common.no_match")}
          </div>
        )}
        {filtered.map((o: any) => (
          <div
            key={o.id}
            className="menu-item"
            onMouseDown={() => {
              addChip(o.id);
              setOpen(false);
              setQ("");
            }}
          >
            {pname(o)}
          </div>
        ))}
      </div>
      {onNew && (
        <div
          className="menu-item menu-item-new"
          onMouseDown={() => {
            setOpen(false);
            setQ("");
            onNew();
          }}
        >
          {newLbl}
        </div>
      )}
    </div>
  );
  if (colKey !== undefined) {
    return (
      <div className="field" style={{ position: "relative" }} ref={ref}>
        <div
          onClick={onToggle}
          style={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "3px 0",
            userSelect: "none",
          }}
        >
          <span
            style={{
              fontSize: FS.s8,
              color: "var(--tm)",
              width: 10,
              textAlign: "center",
              flexShrink: 0,
            }}
          >
            {isOpen ? "▼" : "▶"}
          </span>
          <span style={{ fontSize: FS.s10_5, fontWeight: 500, color: "var(--ts)" }}>
            {l}
          </span>
          {!locked && isOpen && (
            <button
              type="button"
              className="btn btn-ghost"
              style={{
                padding: "0 6px",
                fontSize: FS.s9,
                background: "rgba(74,124,255,.15)",
                color: "var(--ac)",
                border: "1px solid rgba(74,124,255,.3)",
                borderRadius: RAD.r5,
                height: 16,
                lineHeight: "14px",
              }}
              onClick={(e) => {
                e.stopPropagation();
                setOpen((v: any) => !v);
                setQ("");
              }}
            >
              {t("common.add")}
            </button>
          )}
        </div>
        {isOpen && (
          <>
            {chips}
            {drop}
          </>
        )}
      </div>
    );
  }
  return (
    <div className="field" style={{ position: "relative" }} ref={ref}>
      <label
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span>{l}</span>
        {/* BRIEF 108 §8d — a LABELED field row carries the standard AddButton
            on the label's own line (brief 108 §1b's shape), opening the same
            picker the ghost affordance opened; the lock gate is untouched.
            The one unlabeled mount (CaseEventRow.tsx:317, `l=""`) keeps the
            compact ghost: it is an inline row editor with no heading line,
            which is the row §1b's shape is defined on. */}
        {!locked &&
          (l ? (
            <AddButton
              label={t("common.add")}
              onClick={() => {
                setOpen((v: boolean) => !v);
                setQ("");
              }}
            />
          ) : (
            <button
              type="button"
              className="btn btn-ghost"
              style={{ padding: "0 6px", fontSize: FS.s9 }}
              onClick={() => {
                setOpen((v: any) => !v);
                setQ("");
              }}
            >
              {t("common.add")}
            </button>
          ))}
      </label>
      {chips}
      {drop}
    </div>
  );
}
